// Mock data for the "Inbound Flow Report" IFP report.
// Tab 1 ("Inbound Flow Report") baseline metric arrays are transcribed
// directly from the three reference screenshots (28 fiscal weeks,
// 202623 -> 202649 -> 202702), cross-checked for continuity at the
// overlapping columns (202633, 202638) so the default ("All" filters,
// Snapshot Date = "Latest") render matches the design exactly.
// Tab 2 ("Inbound Detail View") is a synthetic PO/SKU leaf-row dataset —
// no screenshot values are hardcoded there since the screenshot's Total
// reflects a specific non-default filter state (Fiscal Week = 202625).

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function shrinkForFilter(name: string, value: string): number {
  const norm = normalizedFromHash(hashString(`${name}:${value}`), 'shrink')
  return 0.4 + norm * 0.35 // [0.4, 0.75]
}

// ---------------------------------------------------------------------------
// Fiscal weeks
// ---------------------------------------------------------------------------

export const WEEK_LABELS: string[] = [
  '202623',
  '202624',
  '202625',
  '202626',
  '202627',
  '202628',
  '202629',
  '202630',
  '202631',
  '202632',
  '202633',
  '202634',
  '202635',
  '202636',
  '202637',
  '202638',
  '202639',
  '202640',
  '202641',
  '202642',
  '202643',
  '202644',
  '202645',
  '202646',
  '202647',
  '202648',
  '202649',
  '202702',
]

// Index at which "Containers (actuals)" goes blank (no actuals recorded yet
// for future weeks) — confirmed identical across all three screenshots.
const CONTAINERS_ACTUALS_CUTOFF_INDEX = 10

export type MetricKey =
  | 'cartons'
  | 'containersActuals'
  | 'containersProjected'
  | 'costDollars'
  | 'cubicFeet'
  | 'skuCount'
  | 'weight'

export const METRIC_KEYS: MetricKey[] = [
  'cartons',
  'containersActuals',
  'containersProjected',
  'costDollars',
  'cubicFeet',
  'skuCount',
  'weight',
]

export const METRIC_LABELS: Record<MetricKey, string> = {
  cartons: 'Cartons',
  containersActuals: 'Containers (actuals)',
  containersProjected: 'Containers (projected)',
  costDollars: 'Cost dollars',
  cubicFeet: 'Cubic feet',
  skuCount: 'SKU count',
  weight: 'Weight (lbs)',
}

const CONTAINERS_ACTUALS_BASE = [226, 136, 132, 189, 228, 218, 91, 73, 37, 6]

export const METRIC_BASELINES: Record<MetricKey, Array<number | null>> = {
  cartons: [
    758040, 680280, 950491, 1052692, 1167142, 1063019, 721753, 605994, 710219,
    538674, 621408, 498711, 505985, 507450, 671250, 579160, 602548, 589119,
    570640, 285430, 196311, 111953, 162229, 86793, 22948, 3154, 2853, 460,
  ],
  containersActuals: [
    ...CONTAINERS_ACTUALS_BASE,
    ...Array(WEEK_LABELS.length - CONTAINERS_ACTUALS_CUTOFF_INDEX).fill(null),
  ],
  containersProjected: [
    206, 180, 222, 279, 338, 281, 172, 152, 188, 146, 160, 144, 136, 145, 186,
    162, 164, 170, 88, 75, 35, 19, 27, 25, 4, 1, 1, 0,
  ],
  costDollars: [
    16513264, 13560084, 20332231, 22357497, 27133152, 17587652, 10862981,
    8929438, 9344505, 6720196, 8567668, 6547726, 7326460, 6837945, 9971719,
    7142893, 7702459, 7124791, 3974974, 3057836, 1997598, 953585, 1290402,
    1406576, 335752, 28307, 144552, 39229,
  ],
  cubicFeet: [
    433310, 378491, 466488, 585077, 710425, 589388, 361605, 318791, 395079,
    307580, 336528, 301827, 284636, 305530, 390576, 339314, 344874, 357868,
    185686, 157113, 72567, 40868, 56239, 53515, 8184, 1510, 2663, 442,
  ],
  skuCount: [
    6437, 5655, 6593, 7824, 7889, 6380, 3836, 4121, 3853, 2920, 3400, 3406,
    3458, 3345, 3718, 3351, 3252, 2650, 1383, 1573, 975, 501, 397, 244, 43, 28,
    3, 1,
  ],
  weight: [
    4548070, 3223044, 3940385, 4826910, 6739555, 5882745, 3511619, 3159524,
    3907116, 3336443, 3163653, 3085493, 2851224, 3352584, 4107719, 3403062,
    3686597, 3916045, 1636423, 1621602, 520685, 320576, 355730, 512544, 51536,
    12388, 25240, 2403,
  ],
}

export function formatMetricCell(key: MetricKey, value: number | null): string {
  if (value == null) return ''
  const formatted = value.toLocaleString('en-US')
  return key === 'costDollars' ? `$${formatted}` : formatted
}

// ---------------------------------------------------------------------------
// Filter option lists
// ---------------------------------------------------------------------------

export const SOURCE_OPTIONS = ['PO', 'JDA', 'Planned']
export const IMPORT_DOMESTIC_OPTIONS = ['Import', 'Domestic']

export const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]

export const SUPPLIER_OPTIONS: string[] = Array.from(
  { length: 24 },
  (_, i) => `Supplier ${1001 + i}`
)

// Fixed anchor so the option list is stable across renders/sessions rather
// than depending on the actual current date.
const SNAPSHOT_ANCHOR = new Date('2026-07-14')

function lastFridayOnOrBefore(date: Date): Date {
  const next = new Date(date)
  const day = next.getDay() // 0=Sun...6=Sat, Fri=5
  const diff = (day - 5 + 7) % 7
  next.setDate(next.getDate() - diff)
  return next
}

function formatSnapshotDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

const RECENT_FRIDAYS: string[] = (() => {
  const fridays: string[] = []
  let cursor = lastFridayOnOrBefore(SNAPSHOT_ANCHOR)
  for (let i = 0; i < 4; i++) {
    fridays.push(formatSnapshotDate(cursor))
    cursor = new Date(cursor)
    cursor.setDate(cursor.getDate() - 7)
  }
  return fridays
})()

export const SNAPSHOT_DATE_OPTIONS = ['Latest', ...RECENT_FRIDAYS]

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

export interface InboundFlowFilterState {
  snapshotDate: string
  season: string | null
  event: string | null
  source: string | null
  divisionId: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  dc: string | null
  importDomestic: string | null
  planId: string | null
  supplier: string | null
  // Detail View only
  fiscalWeek: string | null
  po: string | null
  sku: string | null
}

// Filters backed by real API data (Season, Division/Department/Class/
// SubClass, Plan Name) have no genuine relationship to this mock dataset, so
// — matching the convention already established in costShiftGridMockData.ts /
// poLevelDetailViewMockData.ts / storeCubeCartonUpdateMockData.ts — they only
// contribute a deterministic shrink/wiggle factor rather than literally
// filtering rows.
const TAB1_SHRINK_KEYS: Array<keyof InboundFlowFilterState> = [
  'snapshotDate',
  'season',
  'event',
  'source',
  'divisionId',
  'departmentId',
  'classId',
  'subclassId',
  'dc',
  'importDomestic',
  'planId',
  'supplier',
]

const DETAIL_SHRINK_KEYS: Array<keyof InboundFlowFilterState> = [
  'snapshotDate',
  'season',
  'divisionId',
  'departmentId',
  'classId',
  'subclassId',
  'planId',
]

function activeFilterPairs(
  filters: InboundFlowFilterState,
  keys: Array<keyof InboundFlowFilterState>
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  for (const key of keys) {
    const value = filters[key]
    if (!value) continue
    if (key === 'snapshotDate' && value === 'Latest') continue
    pairs.push([key, String(value)])
  }
  return pairs
}

function totalShrink(pairs: Array<[string, string]>): number {
  return pairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(name, value),
    1
  )
}

// ---------------------------------------------------------------------------
// Tab 1 — pivoted metrics-by-week matrix
// ---------------------------------------------------------------------------

export function buildMetricSeries(
  filters: InboundFlowFilterState
): Record<MetricKey, Array<number | null>> {
  const shrink = totalShrink(activeFilterPairs(filters, TAB1_SHRINK_KEYS))

  const result = {} as Record<MetricKey, Array<number | null>>
  METRIC_KEYS.forEach(key => {
    result[key] = METRIC_BASELINES[key].map(value =>
      value == null ? null : Math.max(0, Math.round(value * shrink))
    )
  })
  return result
}

// ---------------------------------------------------------------------------
// Tab 2 — synthetic PO/SKU leaf rows
// ---------------------------------------------------------------------------

export interface DetailLeafRow {
  id: string
  source: string
  dc: string
  orderNo: string
  sku: string
  estimatedDcDeliveryDate: Date
  cartons: number
  costDollars: number
  cubicFeet: number
  weightLbs: number
  containers: number
  event: string
  importDomestic: string
  supplier: string
  fiscalWeek: string
}

// Base date so that fiscal week 202625 (index 2) lands on 7/19/2026 — the
// exact date shown in the Detail View reference screenshot for that week.
const DETAIL_BASE_DATE = new Date('2026-07-05')

function dateForWeekIndex(index: number): Date {
  const date = new Date(DETAIL_BASE_DATE)
  date.setDate(date.getDate() + index * 7)
  return date
}

export function formatDeliveryDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
}

const PO_GROUP_COUNT = 10

function buildPoGroupRows(groupIndex: number): DetailLeafRow[] {
  const groupId = `pog-${groupIndex}`
  const groupSeed = hashString(groupId)

  const source =
    SOURCE_OPTIONS[
      Math.floor(
        normalizedFromHash(groupSeed, 'source') * SOURCE_OPTIONS.length
      )
    ]
  const dc =
    DC_OPTIONS[
      Math.floor(normalizedFromHash(groupSeed, 'dc') * DC_OPTIONS.length)
    ]
  const importDomestic =
    IMPORT_DOMESTIC_OPTIONS[
      Math.floor(
        normalizedFromHash(groupSeed, 'impdom') * IMPORT_DOMESTIC_OPTIONS.length
      )
    ]
  const supplier =
    SUPPLIER_OPTIONS[
      Math.floor(
        normalizedFromHash(groupSeed, 'supplier') * SUPPLIER_OPTIONS.length
      )
    ]
  const eventNumber =
    1 + Math.floor(normalizedFromHash(groupSeed, 'event') * 10)
  const event = `SEA${String(eventNumber).padStart(2, '0')}`
  const weekIndex = Math.floor(
    normalizedFromHash(groupSeed, 'week') * WEEK_LABELS.length
  )
  const fiscalWeek = WEEK_LABELS[weekIndex]
  const estimatedDcDeliveryDate = dateForWeekIndex(weekIndex)
  const orderNo = String(40000000 + (hashString(groupId + 'po') % 9999999))

  const lineCount = 8 + Math.floor(normalizedFromHash(groupSeed, 'lines') * 9) // 8-16

  return Array.from({ length: lineCount }, (_, lineIndex) => {
    const lineId = `${groupId}-${lineIndex}`
    const lineSeed = hashString(lineId)
    const sku = String(10000000 + (hashString(lineId + 'sku') % 9999999))
    const cartons = Math.round(
      100 + normalizedFromHash(lineSeed, 'cartons') * 450
    )
    const costDollars = Math.round(
      2000 + normalizedFromHash(lineSeed, 'cost') * 12000
    )
    const cubicFeet = Math.round(
      50 + normalizedFromHash(lineSeed, 'cubic') * 750
    )
    const weightLbs = Math.round(
      1000 + normalizedFromHash(lineSeed, 'weight') * 7000
    )

    return {
      id: lineId,
      source,
      dc,
      orderNo,
      sku,
      estimatedDcDeliveryDate,
      cartons,
      costDollars,
      cubicFeet,
      weightLbs,
      containers: 1,
      event,
      importDomestic,
      supplier,
      fiscalWeek,
    }
  })
}

export const ALL_DETAIL_ROWS: DetailLeafRow[] = Array.from(
  { length: PO_GROUP_COUNT },
  (_, i) => i
).flatMap(buildPoGroupRows)

export const EVENT_OPTIONS: string[] = Array.from(
  new Set(ALL_DETAIL_ROWS.map(r => r.event))
).sort()
export const PO_OPTIONS: string[] = Array.from(
  new Set(ALL_DETAIL_ROWS.map(r => r.orderNo))
).sort()
export const SKU_OPTIONS: string[] = Array.from(
  new Set(ALL_DETAIL_ROWS.map(r => r.sku))
).sort()

function matchesRowFilters(
  row: DetailLeafRow,
  filters: InboundFlowFilterState
): boolean {
  if (filters.event && row.event !== filters.event) return false
  if (filters.source && row.source !== filters.source) return false
  if (filters.dc && row.dc !== filters.dc) return false
  if (filters.importDomestic && row.importDomestic !== filters.importDomestic)
    return false
  if (filters.supplier && row.supplier !== filters.supplier) return false
  if (filters.po && row.orderNo !== filters.po) return false
  if (filters.sku && row.sku !== filters.sku) return false
  if (filters.fiscalWeek && row.fiscalWeek !== filters.fiscalWeek) return false
  return true
}

export function buildDetailRows(
  filters: InboundFlowFilterState
): DetailLeafRow[] {
  const shrink = totalShrink(activeFilterPairs(filters, DETAIL_SHRINK_KEYS))
  const filtered = ALL_DETAIL_ROWS.filter(row =>
    matchesRowFilters(row, filters)
  )

  return filtered.map(row => ({
    ...row,
    cartons: Math.max(0, Math.round(row.cartons * shrink)),
    costDollars: Math.max(0, Math.round(row.costDollars * shrink)),
    cubicFeet: Math.max(0, Math.round(row.cubicFeet * shrink)),
    weightLbs: Math.max(0, Math.round(row.weightLbs * shrink)),
  }))
}
